import { ApplicationRef, Injectable, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { first, takeUntil, tap } from 'rxjs/operators';
import { Subject, Subscription, concat, interval } from 'rxjs';
import * as Sentry from '@sentry/browser';
import { NewVersionModalComponent } from '../../utils/modals/new-version-modal/new-version-modal.component';
import { ServiceDestroyed, TakeUntilDestroy } from '../decorators/take-until-destroy';
import { LocalStorageService } from './local-storage.service';

/** How often we ask the service worker for a new version. Value from before the f967f27f6 regression. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Hard-recovery attempt counter key — guards against reload loops. */
export const RECOVERY_ATTEMPTS_KEY = 'versionRecoveryAttempts';

/** Window in which recovery attempts are counted. */
export const RECOVERY_WINDOW_MS = 60 * 60 * 1000;

/** How long the version guard defers to the service worker after it detects a new version. */
export const SW_UPDATE_GRACE_MS = 15 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
@TakeUntilDestroy
export class NewVersionService implements ServiceDestroyed, OnDestroy {
  serviceDestroyed = new Subject<void>();

  private subscriptions = new Subscription();

  private dialogRef: MatDialogRef<NewVersionModalComponent> | null = null;

  private dialogHardRecovery = false;

  /** Raised right before reloading so the beforeunload handler does not block the navigation. */
  reloadInProgress = false;

  private serviceWorkerUpdateStartedAt: number | null = null;

  private reportedToSentry = new Set<string>();

  constructor(
    private swUpdate: SwUpdate,
    private dialog: MatDialog,
    private localStorageService: LocalStorageService,
    private appRef: ApplicationRef,
  ) {
    this.checkForUpdate();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.serviceDestroyed.next();
    this.serviceDestroyed.complete();
  }

  checkForUpdate(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();

    if (!this.swUpdate.isEnabled) {
      console.log('Service worker is not enabled');
      return;
    }

    this.subscriptions.add(
      this.swUpdate.versionUpdates.subscribe(evt => {
        this.localStorageService.setItem('versionEvt', evt);
        switch (evt.type) {
          case 'VERSION_DETECTED':
            console.log(`Downloading new app version: ${evt.version.hash}`);
            this.serviceWorkerUpdateStartedAt = Date.now();
            break;
          case 'VERSION_READY':
            console.log(`Current app version: ${evt.currentVersion.hash}`);
            console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
            this.serviceWorkerUpdateStartedAt = Date.now();
            this.openDialog();
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
            this.serviceWorkerUpdateStartedAt = null;
            this.reportOnce(
              'sw-install-failed',
              `Service worker failed to install version ${evt.version.hash}: ${evt.error}`,
            );
            break;
          case 'NO_NEW_VERSION_DETECTED':
            console.log(`No new app version detected: ${evt.version.hash}`);
            break;
        }
      }),
    );

    this.subscriptions.add(
      this.swUpdate.unrecoverable.subscribe(evt => {
        console.error(`Service worker in unrecoverable state: ${evt.reason}`);
        this.reportOnce('sw-unrecoverable', `Service worker in unrecoverable state: ${evt.reason}`);
        this.openDialog(true);
      }),
    );

    this.subscriptions.add(
      concat(
        this.appRef.isStable.pipe(first(isStable => isStable === true)),
        interval(VERSION_CHECK_INTERVAL_MS),
      ).subscribe(() => {
        this.swUpdate.checkForUpdate().catch(error => console.error('checkForUpdate failed:', error));
      }),
    );
  }

  /**
   * True only while a service-worker-driven update is actually in progress.
   * Once the grace window passes we assume the worker went silent — the version guard
   * then stops deferring to it, since that is exactly the scenario it exists to handle.
   */
  isServiceWorkerHandlingUpdate(): boolean {
    if (this.serviceWorkerUpdateStartedAt === null) {
      return false;
    }

    if (Date.now() - this.serviceWorkerUpdateStartedAt > SW_UPDATE_GRACE_MS) {
      this.serviceWorkerUpdateStartedAt = null;
      return false;
    }

    return true;
  }

  openDialog(hardRecovery = false): void {
    if (this.dialogRef) {
      // Modal already open. We only escalate from soft mode to hard recovery.
      if (!hardRecovery || this.dialogHardRecovery) {
        return;
      }
      this.dialogRef.close();
      this.dialogRef = null;
    }

    this.dialogHardRecovery = hardRecovery;
    this.dialogRef = this.dialog.open(NewVersionModalComponent, {
      disableClose: true,
    });

    this.dialogRef.componentInstance.onReload
      .pipe(
        tap(() => (hardRecovery ? this.recover() : this.refresh())),
        takeUntil(this.serviceDestroyed),
      )
      .subscribe();
  }

  refresh(): void {
    this.swUpdate
      .activateUpdate()
      .then(() => {
        this.localStorageService.removeItem('versionEvt');
        this.reloadInProgress = true;
        document.location.reload();
      })
      .catch(error => {
        console.error('Failed to apply updates:', error);
        this.recover();
      });
  }

  /**
   * Hard recovery: used when the service worker is in an unrecoverable state
   * or when the version guard detected a version mismatch the worker is unaware of.
   * Order matters — unregister first, only then clear the caches.
   */
  async recover(): Promise<void> {
    this.recordRecoveryAttempt();
    this.reportOnce('sw-hard-recovery', 'Hard recovery triggered: unregistering service worker and clearing caches');

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));

      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map(key => caches.delete(key)));

      this.localStorageService.removeItem('versionEvt');
    } catch (error) {
      console.error('Recovery failed, reloading anyway:', error);
    }

    this.reloadInProgress = true;
    document.location.reload();
  }

  /** Records a recovery attempt so the version guard can detect a loop and stop feeding it. */
  private recordRecoveryAttempt(): void {
    const now = Date.now();
    const stored = this.localStorageService.getItem<{ count: number; since: number }>(RECOVERY_ATTEMPTS_KEY);
    const withinWindow = stored && typeof stored.since === 'number' && now - stored.since < RECOVERY_WINDOW_MS;

    this.localStorageService.setItem(RECOVERY_ATTEMPTS_KEY, {
      count: withinWindow ? (stored.count ?? 0) + 1 : 1,
      since: withinWindow ? stored.since : now,
    });
  }

  /** Reports to Sentry once per session for a given key, to avoid flooding the project with events. */
  private reportOnce(key: string, message: string): void {
    if (this.reportedToSentry.has(key)) {
      return;
    }
    this.reportedToSentry.add(key);
    Sentry.captureMessage(message, 'warning');
  }
}
